import {
    useState,
    forwardRef,
    useImperativeHandle,
    useRef,
    useEffect,
} from "react";
import TextInput from "../TextInput";

// Name validator
const nameValidator = (val) => /^[\p{L} '-]{2,50}$/u.test(val.trim());

const generationOptions = [
    { title: "None", key: "" },
    { title: "Jr. (Junior)", key: "Jr." },
    { title: "Sr. (Senior)", key: "Sr." },
    { title: "II (The Second)", key: "II" },
    { title: "III (The Third)", key: "III" },
    { title: "IV (The Fourth)", key: "IV" },
    { title: "V (The Fifth)", key: "V" },
    { title: "Other", key: "Other" },
];

const FullNameInput = forwardRef(({ title, data, readOnly }, ref) => {
    const [error, setError] = useState(false);
    const [generation, setGeneration] = useState(data?.generation || "");
    const [otherGeneration, setOtherGeneration] = useState(data?.otherGeneration || "");

    const firstNameRef = useRef();
    const middleNameRef = useRef();
    const lastNameRef = useRef();
    const otherGenerationRef = useRef();

    const [noMiddleName, setNoMiddleName] = useState(data?.noMiddleName)

    useEffect(() => {
        if (noMiddleName) {
            middleNameRef.current?.resetData()
        }
    }, [noMiddleName])

    useImperativeHandle(ref, () => ({
        validateAndGetData: () => {
            const firstNameData = firstNameRef.current.validateAndGetData();
            const middleNameData = middleNameRef.current.validateAndGetData();
            const lastNameData = lastNameRef.current.validateAndGetData();

            let isValid = firstNameData.isValid && middleNameData.isValid && lastNameData.isValid;
            let otherGenValue = null;

            if (generation === "Other") {
                const otherGenData = otherGenerationRef.current.validateAndGetData();
                isValid = isValid && otherGenData.isValid;
                otherGenValue = otherGenData.value;
            }

            const value = {
                firstName: firstNameData.value,
                middleName: middleNameData.value,
                lastName: lastNameData.value,
                generation: generation,
                otherGeneration: generation === "Other" ? otherGenValue : null,
                noMiddleName,
            };

            setError(!isValid);
            return { isValid, value };
        },
    }));

    return (
        <div className="my-4">
            <label className="block text-base font-medium text-gray-700 mb-4">{title}</label>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {/* First Name */}
                <TextInput
                    title="First Name"
                    ref={firstNameRef}
                    data={data?.firstName}
                    validator={nameValidator}
                    isMandatory={true}
                    disabled={readOnly}
                />

                {/* Middle Name */}
                <div>
                    <TextInput
                        title="Middle Name"
                        ref={middleNameRef}
                        data={data?.middleName}
                        validator={(val) => !val || nameValidator(val)}
                        disabled={readOnly || noMiddleName}
                        isMandatory={!noMiddleName}
                    />
                    <div className="flex items-center space-x-2 mt-1">
                        <input
                            type="checkbox"
                            id="no-middle-name"
                            checked={noMiddleName}
                            onChange={(e) => setNoMiddleName(e.target.checked)}
                        />
                        <label htmlFor="no-middle-name" className="text-sm text-gray-700">
                            I do not have a middle name
                        </label>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-4">
                {/* Last Name */}
                <TextInput
                    title="Last Name"
                    ref={lastNameRef}
                    data={data?.lastName}
                    validator={nameValidator}
                    isMandatory={true}
                    disabled={readOnly}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {/* Generation Dropdown */}
                    <div className={`flex flex-col ${generation !== "Other" ? "md:col-span-2" : ""}`}>
                        <label className="mt-2 text-sm font-medium text-gray-700 mb-1">Generation</label>
                        <select
                            value={generation}
                            onChange={(e) => setGeneration(e.target.value)}
                            disabled={readOnly}
                            className="border border-gray-300 rounded px-3 py-2.5 w-full"
                        >
                            {generationOptions.map((option) => (
                                <option key={option.key} value={option.key}>
                                    {option.title}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Specify Other Field (only when 'Other' is selected) */}
                    {generation === "Other" && (
                        <div className="flex flex-col">
                            <TextInput
                                title="Specify"
                                ref={otherGenerationRef}
                                data={otherGeneration}
                                validator={(val) => val.trim().length > 0}
                                isMandatory={true}
                                disabled={readOnly}
                            />
                        </div>
                    )}
                </div>

            </div>


            {error && (
                <p className="mt-2 text-sm text-red-600">Please fill all required fields correctly.</p>
            )}
        </div>
    );
});

export default FullNameInput;
